angular.module('bizmetryApp').controller('CreateApiCollectionDialogController', function (
    $scope,
    $mdDialog,
    $timeout,
    $q,
    ApiCollectionService,
    TelemetryAgentService,
    DatasourceService,
    dialogService,
    sessionInfo
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    ctrl.steps = ['Import Source', 'Explore Spec', 'Details'];
    ctrl.stepSubtitles = [
        'Choose how to import the OpenAPI spec',
        'Review the imported specification',
        'Set collection name and save'
    ];
    ctrl.currentStep = 0;

    ctrl.sourceType = null;
    ctrl.onlineAgents = [];
    ctrl.loadingAgents = false;
    ctrl.selectedAgent = null;
    ctrl.swaggerPath = '';
    ctrl.isFetching = false;
    ctrl.fetchError = null;
    ctrl.swaggerUrlError = null;
    ctrl._urlDebounce = null;

    ctrl.selectedFile = null;
    ctrl.fileSizeLabel = '';
    ctrl.fileParsedOk = false;
    ctrl.fileParseError = null;
    ctrl.specEndpointsLabel = '';

    ctrl.resolvedSpec = null;
    ctrl.resolvedSpecObj = null;

    // Step 2 — editable
    ctrl.collectionName = '';
    ctrl.collectionDescription = '';
    ctrl.isSaving = false;
    ctrl.saveError = null;

    // Step 2 — read-only (derived from spec)
    ctrl.apiVersion = '';
    ctrl.apiTags = [];
    ctrl.pathCount = 0;

    var URL_PATTERN = /^(?:[\w.-]+|\d{1,3}(?:\.\d{1,3}){3})(?::\d+)?(?:\/[^\s]*)?$/;
    var MAX_FILE_BYTES = 3 * 1024 * 1024; // 3 MB

    // ================================================================
    // INIT
    // ================================================================

    ctrl.$onInit = function () {
        _loadAgents();
    };

    // ================================================================
    // AGENTS
    // ================================================================

    function _loadAgents() {
        ctrl.loadingAgents = true;
        DatasourceService.getReachableAgentsByAccount(sessionInfo.accountId)
            .then(function (agents) {
                ctrl.onlineAgents = agents || [];
            })
            .catch(function (err) {
                console.error('❌ [CreateApiCollection] Error fetching agents:', err);
                ctrl.onlineAgents = [];
            })
            .finally(function () {
                ctrl.loadingAgents = false;
            });
    }

    // ================================================================
    // SOURCE SELECTION
    // ================================================================

    ctrl.selectSource = function (type) {
        if (ctrl.sourceType === type) return;
        ctrl.sourceType = type;
        _resetUrlPanel();
        _resetFilePanel();
        ctrl.fetchError = null;
        ctrl.resolvedSpec = null;
        ctrl.resolvedSpecObj = null;
        ctrl.blankText = null;
    };

    function _resetUrlPanel() {
        if (ctrl._urlDebounce) $timeout.cancel(ctrl._urlDebounce);
        ctrl.selectedAgent = null;
        ctrl.swaggerPath = '';
        ctrl.isFetching = false;
        ctrl.fetchError = null;
        ctrl.swaggerUrlError = null;
    }

    function _resetFilePanel() {
        ctrl.selectedFile = null;
        ctrl.fileSizeLabel = '';
        ctrl.fileParsedOk = false;
        ctrl.fileParseError = null;
        ctrl.specEndpointsLabel = '';
        const input = document.getElementById('cac-file-input');
        if (input) input.value = '';
    }

    // ================================================================
    // OPTION A — Agent URL
    // ================================================================

    ctrl.onAgentChange = function () {
        ctrl.fetchError = null;
    };

    ctrl.onPathChange = function () {
        ctrl.fetchError = null;
        if (ctrl._urlDebounce) $timeout.cancel(ctrl._urlDebounce);
        ctrl._urlDebounce = $timeout(function () {
            ctrl.swaggerUrlError = _validateSwaggerUrl(ctrl.swaggerPath);
        }, 400);
    };

    function _validateSwaggerUrl(path) {
        if (!path || path.trim().length === 0) return null;
        const trimmed = path.trim();
        if (/^https?:\/\//i.test(trimmed)) {
            return 'Do not include the protocol — the "https://" prefix is already added.';
        }
        if (!URL_PATTERN.test(trimmed)) {
            return 'Invalid address. Enter a valid hostname or IP, e.g. my-service.internal/v3/api-docs';
        }
        return null;
    }

    function _fetchFromAgent() {
        const fullUrl = 'https://' + ctrl.swaggerPath.trim();
        const agentId = ctrl.selectedAgent.agentId || ctrl.selectedAgent.id;

        console.log('🌐 [CreateApiCollection] fetchSwagger - agentId:', agentId, '| url:', fullUrl);

        ctrl.isFetching = true;
        ctrl.fetchError = null;

        return TelemetryAgentService.fetchSwaggerContract(agentId, fullUrl)
            .then(function (response) {
                const data = response.data;
                const sanitizedContent = _sanitizeRawSpec(data.rawContent);
                const specObj = _parseSpec(sanitizedContent, data.contentType);
                const validationError = _validateSpecObject(specObj);

                if (validationError) {
                    return $q.reject({ _validationError: validationError });
                }

                ctrl.resolvedSpec = sanitizedContent;
                ctrl.resolvedSpecObj = specObj;
                console.log('✅ [CreateApiCollection] Swagger fetched and validated via agent');
            })
            .catch(function (err) {
                if (err && err._validationError) {
                    dialogService.showErrorDialog(
                        'Invalid Swagger Spec',
                        dialogService.formatErrorMessage({
                            error: 'Invalid OpenAPI / Swagger contract',
                            message: err._validationError,
                            path: fullUrl,
                            timestamp: new Date().toISOString()
                        })
                    );
                } else {
                    const msg = (err.data && err.data.message) ||
                        err.message ||
                        'Could not fetch the spec. Check the URL and try again.';
                    dialogService.showErrorDialog(
                        'Fetch Failed',
                        dialogService.formatErrorMessage({
                            error: 'Remote Fetch Error',
                            message: msg,
                            path: fullUrl,
                            timestamp: new Date().toISOString()
                        })
                    );
                }
                return $q.reject(err);
            })
            .finally(function () {
                ctrl.isFetching = false;
            });
    }

    // ================================================================
    // OPTION B — Local File
    // ================================================================

    ctrl.openFilePicker = function () {
        const input = document.getElementById('cac-file-input');
        if (input) input.click();
    };

    ctrl.onFileSelected = function (inputEl) {
        const file = inputEl.files && inputEl.files[0];
        if (!file) return;

        _resetFilePanel();
        ctrl.selectedFile = file;
        ctrl.fileSizeLabel = _formatBytes(file.size);

        // ── Guard: file too large for in-browser YAML parsing ────────────
        if (file.size > MAX_FILE_BYTES) {
            ctrl.fileParseError = 'The file is too large (' + _formatBytes(file.size) + '). ' +
                'Maximum supported size is 3 MB. Use the URL import option instead.';
            console.warn('⚠️ [CreateApiCollection] File rejected — size:', _formatBytes(file.size));
            return;
        }

        _ensureJsYaml().then(function () {
            const reader = new FileReader();
            reader.onload = function (e) {
                $scope.$apply(function () {
                    try {
                        const raw = e.target.result;
                        const spec = _parseSpec(raw, file.name);

                        if (!spec) {
                            const isYaml = file.name.endsWith('.yml') || file.name.endsWith('.yaml');
                            ctrl.fileParseError = (isYaml && !window.jsyaml)
                                ? 'YAML parsing library could not be loaded. Please check your connection and try again.'
                                : 'Could not parse the file — make sure it is a valid JSON or YAML OpenAPI spec.';
                            return;
                        }

                        const validationError = _validateSpecObject(spec);
                        if (validationError) {
                            ctrl.fileParseError = validationError;
                            ctrl.fileParsedOk = false;
                            return;
                        }

                        ctrl.resolvedSpec = raw;
                        ctrl.resolvedSpecObj = spec;
                        ctrl.fileParsedOk = true;
                        ctrl.specEndpointsLabel = _describeSpec(spec);
                        console.log('✅ [CreateApiCollection] File parsed:', ctrl.specEndpointsLabel);

                    } catch (err) {
                        console.error('❌ [CreateApiCollection] Parse error:', err.message || err);
                        ctrl.fileParseError = 'Could not parse the file — make sure it is a valid JSON or YAML OpenAPI spec.';
                    }
                });
            };

            reader.onerror = function () {
                $scope.$apply(function () {
                    ctrl.fileParseError = 'Failed to read the file. Please try again.';
                });
            };

            reader.readAsText(file);
        });
    };

    ctrl.clearFile = function () {
        _resetFilePanel();
        ctrl.resolvedSpec = null;
        ctrl.resolvedSpecObj = null;
    };

    // ================================================================
    // OPTION C — Create from blank (Swagger editor)
    // ================================================================

    // Minimal but valid OpenAPI 3.0 starting point; the editor requires at
    // least one change before it can be used (title/version are the obvious ones).
    var BLANK_TEMPLATE = {
        openapi: '3.0.3',
        info: {
            title: 'My API',
            version: '1.0.0',
            description: 'Describe what this API does.'
        },
        paths: {
            '/items': {
                get: {
                    operationId: 'listItems',
                    summary: 'List items',
                    responses: {
                        '200': {
                            description: 'OK',
                            content: {
                                'application/json': {
                                    schema: { type: 'array', items: { $ref: '#/components/schemas/Item' } }
                                }
                            }
                        }
                    }
                }
            }
        },
        components: {
            schemas: {
                Item: {
                    type: 'object',
                    properties: {
                        id:   { type: 'string' },
                        name: { type: 'string' }
                    }
                }
            }
        }
    };

    ctrl.blankText   = null;   // editor text (YAML or JSON) once a contract was written
    ctrl.blankFormat = 'yaml';

    ctrl.openBlankEditor = function () {
        $mdDialog.show({
            controller:          'SwaggerEditorDialogController',
            controllerAs:        'ctrl',
            templateUrl:         'home/main-menu/api-collection/swagger-editor/swagger-editor-dialog.html',
            parent:              angular.element(document.body),
            clickOutsideToClose: false,
            escapeToClose:       false,
            multiple:            true,
            locals: {
                collection:  null,
                editorMode:  'create',
                // Re-open on what was written so far (changes are compared against it).
                initialSpec: ctrl.resolvedSpecObj && ctrl.sourceType === 'blank'
                    ? ctrl.resolvedSpecObj : angular.copy(BLANK_TEMPLATE)
            }
        }).then(function (result) {
            if (!result || !result.saved) { return; }
            ctrl.resolvedSpecObj    = result.spec;
            ctrl.resolvedSpec       = result.text;
            ctrl.blankText          = result.text;
            ctrl.blankFormat        = result.format;
            ctrl.specEndpointsLabel = _describeSpec(result.spec);
        }, angular.noop);
    };

    // ================================================================
    // WIZARD NAVIGATION
    // ================================================================

    ctrl.canAdvance = function () {
        if (ctrl.currentStep === 0) {
            if (!ctrl.sourceType) return false;
            if (ctrl.isFetching) return false;
            if (ctrl.sourceType === 'url') {
                return ctrl.selectedAgent !== null &&
                    ctrl.swaggerPath && ctrl.swaggerPath.trim().length > 0 &&
                    !ctrl.swaggerUrlError;
            }
            if (ctrl.sourceType === 'file') return ctrl.fileParsedOk;
            if (ctrl.sourceType === 'blank') return !!ctrl.blankText && ctrl.resolvedSpecObj !== null;
        }
        if (ctrl.currentStep === 1) return ctrl.resolvedSpecObj !== null;
        if (ctrl.currentStep === 2) {
            return ctrl.collectionName && ctrl.collectionName.trim().length > 0 &&
                ctrl.collectionDescription && ctrl.collectionDescription.trim().length > 0;
        }
        return true;
    };

    ctrl.nextStep = function () {
        if (!ctrl.canAdvance()) return;

        if (ctrl.currentStep === 0 && ctrl.sourceType === 'url') {
            _fetchFromAgent().then(function () {
                ctrl.currentStep++;
            });
            return;
        }

        if (ctrl.currentStep === 0 && ctrl.sourceType === 'file') {
            const validationError = _validateSpecObject(ctrl.resolvedSpecObj);
            if (validationError) {
                dialogService.showErrorDialog(
                    'Invalid Swagger Spec',
                    dialogService.formatErrorMessage({
                        error: 'Invalid OpenAPI / Swagger contract',
                        message: validationError,
                        path: ctrl.selectedFile ? ctrl.selectedFile.name : 'local file',
                        timestamp: new Date().toISOString()
                    })
                );
                return;
            }
            ctrl.currentStep++;
            return;
        }

        // Step 1 → Step 2: populate fields from spec
        if (ctrl.currentStep === 1) {
            _populateFromSpec(ctrl.resolvedSpecObj);
            ctrl.currentStep++;
            return;
        }

        if (ctrl.currentStep < ctrl.steps.length - 1) {
            ctrl.currentStep++;
        } else {
            ctrl.submit();
        }
    };

    ctrl.prevStep = function () {
        if (ctrl.currentStep > 0) ctrl.currentStep--;
    };

    // ================================================================
    // STEP 2 — Populate from spec (called on entering step 2)
    // ================================================================

    function _populateFromSpec(spec) {
        if (!spec || !spec.info) return;

        ctrl.collectionName        = spec.info.title       || '';
        ctrl.collectionDescription = spec.info.description || '';

        ctrl.apiVersion = spec.info.version || '';
        ctrl.apiTags    = Array.isArray(spec.tags)
            ? spec.tags.map(function (t) { return { name: t.name, description: t.description || null }; })
            : [];
        ctrl.pathCount  = spec.paths ? Object.keys(spec.paths).length : 0;
    }

    // ================================================================
    // SUBMIT — always multipart to handle large specs safely
    // ================================================================

    ctrl.submit = function () {
        if (!ctrl.collectionName || ctrl.collectionName.trim().length === 0) return;

        ctrl.isSaving = true;
        ctrl.saveError = null;

        var swaggerSource = null;

        if (ctrl.sourceType === 'url') {
            swaggerSource = {
                source_type: 'HTTP_URL',
                source_url: 'https://' + ctrl.swaggerPath.trim(),
                origin_agent: {
                    id: ctrl.selectedAgent.agentId || ctrl.selectedAgent.id,
                    name: ctrl.selectedAgent.agentName || ctrl.selectedAgent.name
                }
            };
        } else if (ctrl.sourceType === 'file') {
            swaggerSource = {
                source_type: 'FILE',
                source_file_name: ctrl.selectedFile ? ctrl.selectedFile.name : null
            };
        } else if (ctrl.sourceType === 'blank') {
            // Written in the Swagger editor: stored as a file source so later
            // edits (Edit Swagger / replace from file) keep working unchanged.
            swaggerSource = {
                source_type: 'FILE',
                source_file_name: _blankFileName()
            };
        }

        var formData = new FormData();
        formData.append('collection_name', ctrl.collectionName.trim());
        formData.append('description', ctrl.collectionDescription.trim());
        formData.append('swagger_source', JSON.stringify(swaggerSource));

        if (ctrl.sourceType === 'file' && ctrl.selectedFile) {
            // Original file → sent as a file part, bypasses SecRequestBodyNoFilesLimit
            formData.append('spec_file', ctrl.selectedFile, ctrl.selectedFile.name);
        } else if (ctrl.sourceType === 'blank') {
            var blankBlob = new Blob([ctrl.blankText], { type: 'text/plain' });
            formData.append('spec_file', blankBlob, _blankFileName());
        } else {
            // URL import: wrap raw spec string in a Blob so it is treated as a file part
            var specBlob = new Blob([ctrl.resolvedSpec], { type: 'text/plain' });
            formData.append('spec_file', specBlob, 'swagger_raw.txt');
        }

        ApiCollectionService.createMultipart(formData)
            .then(function (response) {
                $mdDialog.hide({ created: true, collection: response.data });
            })
            .catch(function (err) {
                const status = err.status;
                const msg = (err.data && err.data.message) ||
                    'Failed to save the collection. Please try again.';

                const title = status === 409 ? 'Duplicate Collection' : 'Save Failed';
                dialogService.showErrorDialog(
                    title,
                    dialogService.formatErrorMessage({
                        error: status === 409 ? 'Conflict' : 'Collection Save Error',
                        message: msg,
                        path: '/api-collections',
                        timestamp: new Date().toISOString()
                    })
                );
            })
            .finally(function () {
                ctrl.isSaving = false;
            });
    };

    ctrl.cancel = function () {
        $mdDialog.cancel();
    };

    // ================================================================
    // SPEC VALIDATION
    // ================================================================

    function _validateSpecObject(spec) {
        if (!spec || typeof spec !== 'object') {
            return 'The content could not be parsed as a valid JSON or YAML document.';
        }
        const isOpenApi3 = typeof spec.openapi === 'string' && spec.openapi.startsWith('3.');
        const isSwagger2 = typeof spec.swagger === 'string' && spec.swagger.startsWith('2.');
        if (!isOpenApi3 && !isSwagger2) {
            return 'The document does not appear to be a valid OpenAPI (3.x) or Swagger (2.x) contract. ' +
                'Missing required "openapi" or "swagger" version field.';
        }
        if (!spec.info || typeof spec.info !== 'object') {
            return 'The contract is missing the required "info" section.';
        }
        if (!spec.info.title || typeof spec.info.title !== 'string') {
            return 'The contract "info" section is missing a valid "title".';
        }
        if (!spec.paths || typeof spec.paths !== 'object') {
            return 'The contract is missing the required "paths" section. ' +
                'Make sure the URL points to the OpenAPI spec (e.g. /v3/api-docs), not to the Swagger UI.';
        }
        if (Object.keys(spec.paths).length === 0) {
            return 'The contract has an empty "paths" section — no API endpoints were found.';
        }
        return null;
    }

    // ================================================================
    // HELPERS
    // ================================================================

    function _ensureJsYaml() {
        if (window.jsyaml) return $q.resolve();

        return $q(function (resolve) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/js-yaml/4.1.0/js-yaml.min.js';
            script.onload = function () {
                console.log('✅ [CreateApiCollection] js-yaml loaded on demand');
                resolve();
            };
            script.onerror = function () {
                console.error('❌ [CreateApiCollection] Failed to load js-yaml from CDN');
                resolve(); // resolve anyway — _parseSpec will handle null
            };
            document.head.appendChild(script);
        });
    }

    function _sanitizeRawSpec(raw) {
        if (!raw) return raw;
        // Remove non-printable characters rejected by js-yaml.
        // Keeps: tab (\x09), LF (\x0A), CR (\x0D), printable ASCII (\x20-\x7E),
        // and valid Unicode (\xA0+). Strips control chars that appear in
        // code samples embedded in OpenAPI x-oaiMeta extensions.
        return raw.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '');
    }

    function _parseSpec(raw, hint) {
        if (!raw) return null;
        const h = (hint || '').toLowerCase();
        try {
            if (h.includes('yaml') || h.includes('yml')) {
                if (!window.jsyaml) {
                    console.error('❌ [CreateApiCollection] window.jsyaml is not loaded');
                    return null;
                }
                return window.jsyaml.load(raw);
            }
            try { return JSON.parse(raw); }
            catch (_) {
                if (!window.jsyaml) {
                    console.error('❌ [CreateApiCollection] window.jsyaml is not loaded');
                    return null;
                }
                return window.jsyaml.load(raw);
            }
        } catch (e) {
            console.error('❌ [CreateApiCollection] _parseSpec threw:', e.message || e);
            return null;
        }
    }

    function _blankFileName() {
        return 'swagger-editor.' + (ctrl.blankFormat === 'json' ? 'json' : 'yaml');
    }

    function _formatBytes(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(2) + ' MB';
    }

    function _describeSpec(spec) {
        if (!spec || typeof spec !== 'object') return 'Spec loaded';
        const paths = spec.paths ? Object.keys(spec.paths).length : 0;
        const title = spec.info && spec.info.title ? spec.info.title : 'OpenAPI';
        return title + ' — ' + paths + ' path' + (paths !== 1 ? 's' : '');
    }
});